import { TranslateService } from '@ngx-translate/core';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, ValidationErrors, Validators } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Observable } from 'rxjs';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { Organization } from 'src/app/core/model/organization';
import { OrganizationService } from 'src/app/core/service/organization.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { UserService } from 'src/app/core/service/user.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { TextProcessor } from 'src/app/core/utility/text.processor';
import { OrganizationValidators } from 'src/app/core/validators/organization.validators';
import { User } from 'src/app/user/model/user';
import { Extension } from '../model/extension';
import { groupBy, GroupResult } from '@progress/kendo-data-query';
import { sortBy } from 'lodash';
import { ChannelService } from 'src/app/core/service/channel.service';
import { TabStripComponent } from '@progress/kendo-angular-layout';
import { ExtensionService } from 'src/app/core/service/extension.service';

@Component({
  standalone: false,
  selector: 'app-extension-edit',
  templateUrl: './extension-edit.component.html',
  styleUrls: ['./extension-edit.component.scss']
})
export class ExtensionEditComponent implements OnInit
{
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;

  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<Extension> = new EventEmitter();

  public editForm: any;
  public editFormActive = false;
  public organizations = [];
  public model = {};
  public fields: FormlyFieldConfig[] = [];
  public patientLabel = {};
  public inValueChanges = false;
  public searchTerms = '';
  public allSelectableChannels = [];
  public channelsFiltered: Organization[] = [];
  public virtualChannel: any = { itemHeight: 30 };
  public noChannelSelected: string;
  public groupedChannels: GroupResult[] = [];
  public extensionCategoryCannotBeChanged: string;

  /**
   * Current logged in user
   */
  private currentUser: User;
  private currentUserOrganization: Organization;
  private loadedExtension: Extension;

  constructor(
    private fb: UntypedFormBuilder,
    private messageHandlerService: MessageHandlerService,
    private settingService: SettingService,
    private channelService: ChannelService,
    private translate: TranslateService,
    private organizationService: OrganizationService,
    private organizationValidators: OrganizationValidators,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private extensionService: ExtensionService,)
  { }

  public get enabled(): UntypedFormControl
  {
    return this.editForm.get('enabled');
  }

  public get enabledNextAvailable(): UntypedFormControl
  {
    return this.editForm.get('enabledNextAvailable');
  }

  public get allOrganizations(): UntypedFormControl
  {
    return this.editForm.get('allOrganizations');
  }

  public get allChannels(): UntypedFormControl
  {
    return this.editForm.get('allChannels');
  }

  public get channels(): UntypedFormControl
  {
    return this.editForm.get('channels');
  }

  public get required(): UntypedFormControl
  {
    return this.editForm.get('required');
  }

  public get billableItem(): UntypedFormControl
  {
    return this.editForm.get('billableItem');
  }

  public get key(): UntypedFormControl
  {
    return this.editForm.get('key');
  }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name');
  }

  public get category(): UntypedFormControl
  {
    return this.editForm.get('category');
  }

  public get organization(): UntypedFormControl
  {
    return this.editForm.get('organization');
  }

  @Input() public set formModel(extension: Extension)
  {
    this.initModel(extension);
  }

  public ngOnInit(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
      }
    });

    this.organizationService.getCachedOrganizations().subscribe({
      next: (data) =>
      {
        this.organizations = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });

    this.translate.get(['NO-CHANNELS-SELECTED', 'CANNOT-CHANGE-SETTING-EXTENSION-CATEGORY']).subscribe({
      next: (res) =>
      {
        this.noChannelSelected = res['NO-CHANNELS-SELECTED'];
        this.extensionCategoryCannotBeChanged = res['CANNOT-CHANGE-SETTING-EXTENSION-CATEGORY'];
      }
    });
  }

  /**
   * Disable inactive items from drop-down.
   *
   * @param itemArgs Item object
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  /**
   * Save handler
   *
   * @param e Event
   */
  public onSave(e: Event): void
  {
    e.preventDefault();
    const objectToPersist = this.editForm.value;

    // When editing and saving, if Category was changed then check if there are current Community Services or Organizations using it and, if so, do not allow saving
    if (!this.isNew && (this.loadedExtension.category === 'COMMUNITY_SERVICE' || this.loadedExtension.category === 'ORGANIZATION') && (this.loadedExtension.category !== objectToPersist.category))
    {
      // Check if in use
      this.extensionService.isExtensionByCategoryInUse(this.loadedExtension.id, this.loadedExtension.category).subscribe({
        next: (inUseResponse) =>
        {
          if (inUseResponse)
          {
            this.messageHandlerService.displayDirectMessage('error', this.extensionCategoryCannotBeChanged);
            this.tabstrip.selectTab(1);
            return;
          }
          else
          {
            this.performExtensionSave(objectToPersist);
          }
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    else
    {
      this.performExtensionSave(objectToPersist);
    }
  }

  public performExtensionSave(objectToPersist: any): void
  {
    if (this.allOrganizations.value) 
    {
      this.organization.setValue(null);
      this.required.setValue(false);
      if (this.category.value === 'COMMUNITY_SERVICE' || this.category.value === 'ORGANIZATION') 
      {
        objectToPersist.allOrganizations = this.allOrganizations.value
      }
    }

    if (!this.allOrganizations.value || this.category.value !== 'PATIENT') 
    {
      objectToPersist.enabledNextAvailable = null;
    }

    if (objectToPersist.enabledNextAvailable)
    {
      if (objectToPersist.allChannels) 
      {
        objectToPersist.channels = [];
      }
      else
      {
        //objectToPersist.channels = this.channels.value.map(x => ({ channelId: x }));
        if (!objectToPersist.allChannels && objectToPersist.channels.length === 0)
        {
          this.messageHandlerService.displayDirectMessage('error', this.noChannelSelected);
          this.tabstrip.selectTab(1);
          return;
        }

        const channels = [];
        if (this.editForm.controls.channels.value !== null && this.editForm.controls.channels.value.length > 0) 
        {
          this.editForm.controls.channels.value.forEach((channel: any) =>
          {
            let currentId = null;
            let currentVersion = 0;
            if (this.loadedExtension.channels) 
            {
              const existingOrganization: any = this.loadedExtension.channels.find(x => x.channelId === channel.id);
              if (existingOrganization)
              {
                currentId = existingOrganization.id;
                currentVersion = existingOrganization.version;
              }
            }
            channels.push({
              id: currentId,
              version: currentVersion,
              channelId: channel.id,
              settingExtensionId: this.loadedExtension.id !== undefined ? this.loadedExtension.id : 0
            });
          });
          objectToPersist.channels = channels;
        }
      }
    }
    else 
    {
      objectToPersist.allChannels = null;
      objectToPersist.channels = [];
    }

    objectToPersist.organization = this.organization.value ? this.organization.value : null;

    this.save.emit(objectToPersist);
    this.editFormActive = false;
  }

  /**
   * Cancel handler
   *
   * @param e Event
   */
  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  public keyOrgChange(field: string): void
  {
    if (field === 'key' && !this.key.hasError('keyExists'))
    {
      if (this.organization.hasError('keyExists'))
      {
        this.organization.setErrors(null);
      }
      if (this.category.hasError('keyExists'))
      {
        this.category.setErrors(null);
      }
    }
    if (field === 'organization' && !this.organization.hasError('keyExists'))
    {
      if (this.key.hasError('keyExists'))
      {
        this.key.setErrors(null);
      }
      if (this.category.hasError('keyExists'))
      {
        this.category.setErrors(null);
      }
    }
    if (field === 'category' && !this.category.hasError('keyExists'))
    {
      if (this.organization.hasError('keyExists'))
      {
        this.organization.setErrors(null);
      }
      if (this.key.hasError('keyExists'))
      {
        this.key.setErrors(null);
      }
    }
  }

  public validateKey(e: KeyboardEvent): void
  {
    TextProcessor.validateKeyWithUnderscoreAndDashNoSpaces(e);
    this.key.setValue(this.key.value.replaceAll(/ /g, ''));   // To handle paste that contains spaces
  }

  public isOrganizationRequired(): boolean
  {
    return this.editForm && !this.allOrganizations.value;
  }

  /**
   * Filters the channels based on user input.
   */
  public filterChannels(filter: string): void
  {
    this.searchTerms = filter;
    this.channelsFiltered = this.allSelectableChannels
      .filter(channel => channel.name.toLowerCase().includes(filter.toLowerCase()));
    this.setGroupedChannels();
  }

  public channelItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return itemArgs.dataItem.status !== 'Online';
  }

  /**
   * Closing edit form
   */
  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }

  /**
   * Model initilization
   *
   * @param extension Extension edit object.
   */
  private initModel(extension: Extension): void
  {
    if (extension === undefined)
    {
      return;
    }

    this.channelService.getCacheMinimalChannels('?dashboardEnrollmentOnly=true').subscribe({
      next: (data) =>
      {
        this.allSelectableChannels = data;
        this.allSelectableChannels.forEach(c =>
        {
          c.programName = c.program.name;
        });
        this.channelsFiltered = this.allSelectableChannels.sort((a, b) => (a.name.localeCompare(b.name)));
        this.setGroupedChannels();

        this.currentUser = this.authenticationService.currentUser;
        if (this.currentUser)
        {
          this.userService.getUserById(this.currentUser.id, false).subscribe({
            next: (user) =>
            {
              this.currentUserOrganization = user.organization;

              const channelsSelection: any[] = [];
              if (extension.channels !== null && extension.channels !== undefined)
              {
                extension.channels.forEach((channel: any) =>
                {
                  const fullChannel: any = this.allSelectableChannels.find(x => x.id === channel.channelId);
                  channelsSelection.push(fullChannel);
                });
              }

              // Set form
              this.editForm = this.fb.group({
                id: [extension.id !== null ? extension.id : 0],
                key: [extension.key !== null ? extension.key : '', Validators.required],
                name: [extension.name !== null ? extension.name : '', Validators.required],
                enabled: [extension.enabled !== null ? extension.enabled : true, Validators.required],
                enabledNextAvailable: [extension.enabledNextAvailable !== null ? extension.enabledNextAvailable : null],
                allOrganizations: [extension.allOrganizations !== null ? extension.allOrganizations : false],
                allChannels: [extension.allChannels !== null ? extension.allChannels : false],
                channels: [{ value: channelsSelection, disabled: false }],
                required: [extension.required !== null ? extension.required : false, Validators.required],
                category: [extension.category !== '' ? extension.category : 'CARE_TEAM', Validators.required],
                organization: [extension.organization && extension.organization.id !== 0 ? extension.organization : (this.isNew ? this.currentUserOrganization : null)],
                billableItem: [extension.billableItem !== null ? extension.billableItem : false, Validators.required]                
              });

              const keyAsyncOrganizationValidators = [(): Observable<ValidationErrors | null> =>
                this.organizationValidators.validateOrganizationKeyUnique(this.key, this.organization.value ?
                  this.organization.value.id : null, extension.id, this.category.value)
              ];

              this.key.setAsyncValidators(keyAsyncOrganizationValidators);
              this.key.updateValueAndValidity();

              this.organization.setAsyncValidators(keyAsyncOrganizationValidators);
              this.organization.updateValueAndValidity();

              this.category.setAsyncValidators(keyAsyncOrganizationValidators);
              this.category.updateValueAndValidity();

              this.setControlsState();

              this.editForm.get('allOrganizations').valueChanges.subscribe({ next: () => { this.setControlsState(); } });
              this.editForm.get('category').valueChanges.subscribe({ next: () => { this.setControlsState(); } });

              this.loadedExtension = extension;
              this.editFormActive = true;
            }
          });
        }
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      }
    });
  }

  private setControlsState(): void
  {
    if (!this.inValueChanges)   // To prevent recursivity
    {
      this.inValueChanges = true;
      if (this.category && this.category.value)
      {
        if (this.category.value === 'COMMUNITY_SERVICE' || this.category.value === 'ORGANIZATION')
        {
          this.allOrganizations.disable();
          this.allOrganizations.setValue(true, { emitEvent: false });
        }
        else
        {
          this.allOrganizations.enable();
        }
      }
      else
      {
        this.allOrganizations.disable();
        this.allOrganizations.setValue(false, { emitEvent: false });
      }

      if (this.allOrganizations.value)
      {
        this.organization.disable();
        this.organization.setValidators([]);
        this.organization.setValue(null, { emitEvent: false });
      }
      else
      {
        this.organization.enable();
        this.organization.setValidators([Validators.required]);
      }
      this.allOrganizations.updateValueAndValidity();
      this.organization.updateValueAndValidity();
      this.inValueChanges = false;
    }
  }

  private getChannels(): void
  {
    this.channelService.getCacheMinimalChannels().subscribe({
      next: (data) =>
      {
        this.allSelectableChannels = data;
        this.allSelectableChannels.forEach(c =>
        {
          c.programName = c.program.name;
        });
        this.channelsFiltered = this.allSelectableChannels.sort((a, b) => (a.name.localeCompare(b.name)));
        this.setGroupedChannels();
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      }
    });
  }

  private setGroupedChannels(): void
  {
    // Build the grouped Channels
    const groupedChannels: any = groupBy(this.channelsFiltered, [{ field: 'programName' }]);
    this.groupedChannels.forEach(d =>
    {
      const items = sortBy(d.items, ['name']);
      d.items = items;
    });
    this.groupedChannels = sortBy(groupedChannels, ['value']);
  }
}

